import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CheckoutPatientAddress,
  CheckoutPatientAttributes,
  CheckoutPatientData,
  CheckoutPaymentResponse
} from 'components/modules/patientArea/products/types'
import { createPatientData } from 'redux/thunks/create-patient-data'
import nestApiService from 'services/api/nest-api-service'
import { RecordOfAny } from 'types'
import { getOpenJwtToken } from 'utils/getOpenJwtToken'

interface WeightOnboardingState {
  isLoading: boolean
  patientAttributes: CheckoutPatientAttributes | null
  patientAddress: CheckoutPatientAddress | null
  patientData: CheckoutPatientData | null
  paymentResponse: CheckoutPaymentResponse | null
  appliedCoupon: { pix: number; creditCard: number } | null
}

const initialState: WeightOnboardingState = {
  isLoading: false,
  patientAttributes: null,
  patientAddress: null,
  patientData: null,
  paymentResponse: null,
  appliedCoupon: null
}

const weightOnboardingSlice = createSlice({
  name: 'weight-onboarding',
  initialState,
  reducers: {
    setPatientAttributes: (state, { payload }: PayloadAction<CheckoutPatientAttributes | null>) => {
      state.patientAttributes = payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(createPatientData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createPatientData.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.patientData = payload
      })
      .addCase(createPatientData.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(weightOnboardingPayment.pending, (state) => {
        state.isLoading = true
      })
      .addCase(weightOnboardingPayment.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(weightOnboardingPayment.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(applyWeightCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(applyWeightCoupon.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.appliedCoupon = payload
      })
      .addCase(applyWeightCoupon.rejected, (state) => {
        state.isLoading = false
        state.appliedCoupon = null
      })
})

export const { setPatientAttributes } = weightOnboardingSlice.actions

export default weightOnboardingSlice.reducer

interface WeightOnboardingParams {
  orderData: RecordOfAny
}

export const weightOnboardingPayment = createAsyncThunk(
  'weight-payment',
  async ({ orderData }: WeightOnboardingParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post('/weight/payment', orderData, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const weightPaymentForLogged = createAsyncThunk(
  'weight-loggedPayment',
  async ({ orderData }: WeightOnboardingParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post('/weight/loggedPayment', orderData, {
        headers: {
          Authorization: token
        }
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface ApplyWeightCouponParams {
  couponCode: string
  slug: string
}

export const applyWeightCoupon = createAsyncThunk(
  'apply-weight-coupon',
  async ({ couponCode, slug }: ApplyWeightCouponParams, { rejectWithValue }) => {
    try {
      const token = await getOpenJwtToken()

      const { data } = await nestApiService.apiInstance.post(
        'weight/apply-coupon',
        { couponCode, slug },
        {
          headers: {
            Authorization: token
          }
        }
      )

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
